"use strict";
/**
 * Data-driven markdown highlight wiring for the Use Case Details
 * markdown reader.
 *
 * Given the highlight items returned by
 * `GET /api/ai/use-cases/:useCaseId/highlights`, this module builds
 * a `components` map for `@uiw/react-markdown-preview` that wraps
 * every alias surface variant with `<HighlightedTerm>` — passing
 * `elementId` and `useCaseId` so the popup resolves real backend
 * data.
 *
 * When the caller has no highlights yet (initial load, or extraction
 * not run), the components map renders the markdown verbatim with no
 * wrapping. There is no random demo fallback any more.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_MARKDOWN_HIGHLIGHT_COMPONENTS = exports.buildMarkdownHighlightComponents = exports.MARKDOWN_NON_DOM_PROPS = void 0;
var react_1 = __importDefault(require("react"));
var components_1 = require("../../shared/components");
/** Map UI popup kind onto the visual variant of the inline indicator. */
var KIND_TO_VARIANT = {
    'business-impact': 'definition',
    persona: 'persona',
    metric: 'metric',
    technology: 'info',
};
/**
 * Priority for resolving alias collisions when the same surface text
 * (e.g. "HR & Manager Productivity") is referenced by elements of
 * different types. Higher value wins. Metrics, risks and pain points
 * are the most semantically loaded categories, so they outrank
 * persona/tech/data when both claim the same phrase.
 *
 * See `docs/use-case-extraction/extraction-repair-strategy.md` (B5).
 */
var ELEMENT_TYPE_PRIORITY = {
    metric: 100,
    risk: 90,
    pain: 80,
    benefit: 70,
    persona: 60,
    tech: 50,
    data: 40,
    integration: 30,
    budget: 20,
    roadmap: 10,
};
/**
 * Refines the inline marker color for business-impact sub-types so
 * pain points (orange), risks (red) and benefits (green) read
 * distinctly even before the popup opens. Falls back to the broader
 * `kind` mapping for everything else.
 */
var resolveVariant = function (item) {
    if (item.elementType === 'pain')
        return 'pain-point';
    if (item.elementType === 'risk')
        return 'risk';
    if (item.elementType === 'benefit')
        return 'benefit';
    return KIND_TO_VARIANT[item.kind];
};
/**
 * `react-markdown` forwards a few non-DOM props (e.g. `isHeader` on
 * `td`, `ordered` on `li`/`ul`, `node`, `inline`, `level`, `index`,
 * `siblingCount`) to custom component overrides. Strip them before
 * spreading onto the DOM element so React doesn't warn about unknown
 * attributes.
 */
exports.MARKDOWN_NON_DOM_PROPS = [
    'node',
    'inline',
    'ordered',
    'isHeader',
    'level',
    'index',
    'siblingCount',
    'depth',
    'checked',
    'sourcePosition',
];
/**
 * Build a lower-case alias -> item lookup used by the renderTerm
 * callback. When the same surface text resolves to multiple
 * elements, the higher-priority element type wins (see
 * `ELEMENT_TYPE_PRIORITY`) — so e.g. "HR & Manager Productivity"
 * resolves to the metric, not the persona that mistakenly listed it
 * as an alias.
 */
var buildAliasIndex = function (items) {
    var index = new Map();
    for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
        var item = items_1[_i];
        for (var _a = 0, _b = item.aliases; _a < _b.length; _a++) {
            var alias = _b[_a];
            var key = alias.toLowerCase();
            var existing = index.get(key);
            if (!existing ||
                ELEMENT_TYPE_PRIORITY[item.elementType] > ELEMENT_TYPE_PRIORITY[existing.item.elementType]) {
                index.set(key, { alias: alias, item: item });
            }
        }
    }
    return index;
};
var buildTermDefinitions = function (items) {
    var seen = new Set();
    var out = [];
    for (var _i = 0, items_2 = items; _i < items_2.length; _i++) {
        var item = items_2[_i];
        for (var _a = 0, _b = item.aliases; _a < _b.length; _a++) {
            var alias = _b[_a];
            var key = alias.toLowerCase();
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            out.push({ term: alias, variant: resolveVariant(item) });
        }
    }
    return out;
};
var stripNonDomProps = function (rest) {
    var domProps = {};
    Object.keys(rest).forEach(function (k) {
        if (exports.MARKDOWN_NON_DOM_PROPS.indexOf(k) === -1) {
            domProps[k] = rest[k];
        }
    });
    return domProps;
};
/**
 * Returns a fresh components map keyed for the supplied highlights.
 * Memoize on the caller side keyed by `(useCaseId, items)` so the
 * regex / index aren't rebuilt on every render.
 */
exports.buildMarkdownHighlightComponents = function (options) {
    var items = options.items, useCaseId = options.useCaseId;
    var definitions = buildTermDefinitions(items);
    var matcher = components_1.buildTermMatcher(definitions);
    var aliasIndex = buildAliasIndex(items);
    /**
     * Element ids already highlighted within the current `<h2>`/`<h3>`
     * section. Reset every time we render a heading so each section
     * gets its own dedupe window. Cuts visual noise from repeated
     * tokens like "Teams" or "HRIS" while still letting each section
     * surface its key entities once. See repair strategy doc B1.
     */
    var seenInSection = new Set();
    var renderTerm = function (match, key) {
        var entry = aliasIndex.get(match.toLowerCase());
        if (!entry) {
            return match;
        }
        if (seenInSection.has(entry.item.elementId)) {
            return match;
        }
        seenInSection.add(entry.item.elementId);
        var variant = resolveVariant(entry.item);
        return (react_1.default.createElement(components_1.HighlightedTerm, { key: key, term: match, variant: variant, popupKind: entry.item.kind, elementId: entry.item.elementId, useCaseId: useCaseId, hoverHint: entry.item.canonicalName }));
    };
    var wrapMarkdownTag = function (Tag) {
        var Wrapped = function (props) {
            var children = props.children, rest = __rest(props, ["children"]);
            var domProps = stripNonDomProps(rest);
            var wrapped = matcher ? components_1.wrapTermsInChildren(children, matcher, renderTerm) : children;
            return react_1.default.createElement(Tag, domProps, wrapped);
        };
        return Wrapped;
    };
    var wrapHeadingTag = function (Tag) {
        var Wrapped = function (props) {
            // Crossing a section boundary — every heading clears the
            // dedupe window so the next section starts fresh.
            seenInSection.clear();
            var children = props.children, rest = __rest(props, ["children"]);
            var domProps = stripNonDomProps(rest);
            return react_1.default.createElement(Tag, domProps, children);
        };
        return Wrapped;
    };
    return {
        h1: wrapHeadingTag('h1'),
        h2: wrapHeadingTag('h2'),
        h3: wrapHeadingTag('h3'),
        h4: wrapHeadingTag('h4'),
        p: wrapMarkdownTag('p'),
        li: wrapMarkdownTag('li'),
        td: wrapMarkdownTag('td'),
    };
};
/**
 * No-op components map used while highlights are loading or absent —
 * keeps the markdown structure intact without wrapping any words.
 */
exports.EMPTY_MARKDOWN_HIGHLIGHT_COMPONENTS = exports.buildMarkdownHighlightComponents({
    items: [],
});
